import { pick } from 'ramda';
import { batch } from 'react-redux';
import { useNavigate } from 'js/hooks/navigate';
import { useTranslations } from 'js/hooks/i18n';
import React, { memo, useCallback } from 'react';
import { useComponentConfig } from 'js/hooks/component';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import { IGamesGridConfig } from '../../types';
import { updateGamesFeedName, setActiveTab, resetFilters } from '../../store/actions';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../../store';

import style from './GamesTabs.scss';

export const GamesTabsBare: React.FC = () => {
    const navigate = useNavigate();
    const translate = useTranslations();
    const dispatch = useDynamicReducerDispatch();
    const { gamesGridTabs } = useComponentConfig<IGamesGridConfig>();
    const { isLoading, activeTab } = useDynamicReducerSelector(pick(['isLoading', 'activeTab']));

    const handleTabClick = useCallback((tabName: string): void => {
        const currentTab = gamesGridTabs?.find((tab) => tab.tabName === tabName);

        if (currentTab?.pageURL) {
            navigate(currentTab.pageURL);

            return;
        }

        if (currentTab?.tabGameFeed) {
            batch(() => {
                dispatch(setActiveTab(currentTab.tabName));
                dispatch(updateGamesFeedName(currentTab.tabGameFeed));
                dispatch(resetFilters());
            });
        }
    }, [gamesGridTabs, navigate, dispatch]);

    if (!gamesGridTabs || !gamesGridTabs.length) {
        return null;
    }

    return (
        <ul className={`${style.tabContainer} cms-games-grid-tabs`}>
            {gamesGridTabs.map((tab) => (
                <AccessibilityElement
                    tagName='li'
                    key={tab.tabName}
                    className={`${style.tab} ${activeTab === tab.tabName ? style.active : ''} ${isLoading ? style.disabled : ''} cms-games-grid-tab-${tab.tabName}`}
                    onClick={() => !isLoading && handleTabClick(tab.tabName)}
                >
                    <span className={style.tabContent}>{translate(`playtech.mobile.system.games-grid.tabs.${tab.tabName}`, tab.tabName)}</span>
                </AccessibilityElement>
            ))}
        </ul>
    );
};

export const GamesTabs = memo(GamesTabsBare);
